import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelSelect as _vModelSelect, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "app-form-field" }
const _hoisted_2 = { class: "app-form-field__label app-form-field__label--required" }
const _hoisted_3 = { class: "app-form-field__wrapper" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "app-form-field__error"
}
const _hoisted_6 = { class: "app-form-field" }
const _hoisted_7 = { class: "app-form-field__label app-form-field__label--required" }
const _hoisted_8 = { class: "app-form-field__wrapper" }
const _hoisted_9 = ["placeholder"]
const _hoisted_10 = { class: "app-form-field__meta" }
const _hoisted_11 = {
  key: 0,
  class: "app-form-field__error"
}
const _hoisted_12 = { class: "app-form-field" }
const _hoisted_13 = { class: "app-form-field__label app-form-field__label--required" }
const _hoisted_14 = { class: "app-form-field__wrapper" }
const _hoisted_15 = { value: "" }
const _hoisted_16 = { value: "LOW" }
const _hoisted_17 = { value: "NORMAL" }
const _hoisted_18 = { value: "HIGH" }
const _hoisted_19 = { value: "CRITICAL" }
const _hoisted_20 = {
  key: 0,
  class: "app-form-field__error"
}
const _hoisted_21 = {
  key: 0,
  class: "note-form__error"
}
const _hoisted_22 = { class: "modal-footer" }
const _hoisted_23 = ["disabled"]
const _hoisted_24 = {
  key: 0,
  class: "loading-spinner"
}

import { ref, reactive, computed, watch } from 'vue';
// @ts-ignore
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '@/components/base/Modal.vue';
import type { Note } from '@/types/services';

interface Props {
  note: Note | null;
  projectId: string | number;
}

interface Emits {
  (e: 'save', noteData: NoteData): void;
  (e: 'close'): void;
}

interface NoteData {
  title: string;
  content: string;
  priority: string;
  projectId: string | number;
}

interface FormErrors {
  title: string;
  content: string;
  priority: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'NoteModal',
  props: {
    note: { default: null },
    projectId: {}
  },
  emits: ["save", "close"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

const emit = __emit;

const { t } = useI18n();

// Form data
const formData = reactive<NoteData>({
  title: '',
  content: '',
  priority: '',
  projectId: props.projectId
});

// Form state
const loading = ref<boolean>(false);
const submitError = ref<string>('');

const errors = reactive<FormErrors>({
  title: '',
  content: '',
  priority: ''
});

// Computed properties
const isEditing = computed<boolean>(() => !!props.note);

const isFormValid = computed<boolean>(() => {
  return !!(formData.title.trim() &&
         formData.content.trim() &&
         formData.priority &&
         !Object.values(errors).some((error: any) => error.length > 0));
});// Validation functions
const validateTitle = (): void => {
  if (!formData.title.trim()) {
    errors.title = t('notes.validation.titleRequired');
  } else if (formData.title.length > 255) {
    errors.title = t('notes.validation.titleTooLong');
  } else {
    errors.title = '';
  }
};

const validateContent = (): void => {
  if (!formData.content.trim()) {
    errors.content = t('notes.validation.contentRequired');
  } else if (formData.content.length > 2000) {
    errors.content = t('notes.validation.contentTooLong');
  } else {
    errors.content = '';
  }
};

const validatePriority = (): void => {
  if (!formData.priority) {
    errors.priority = t('notes.validation.priorityRequired');
  } else {
    errors.priority = '';
  }
};

const validateForm = (): void => {
  validateTitle();
  validateContent();
  validatePriority();
};

// Watch for form changes to validate
watch(() => formData.title, validateTitle);
watch(() => formData.content, validateContent);
watch(() => formData.priority, validatePriority);

// Handle form submission
const handleSubmit = async (): Promise<void> => {
  validateForm();

  if (!isFormValid.value) {
    return;
  }

  try {
    loading.value = true;
    submitError.value = '';

    const noteData: NoteData = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      priority: formData.priority,
      projectId: props.projectId
    };

    emit('save', noteData);

  } catch (error: any) {
    console.error('Error saving note:', error);
    submitError.value = error.message || t('notes.errorSaving');
  } finally {
    loading.value = false;
  }
};

// Initialize form data when editing
const initializeForm = (): void => {
  if (props.note) {
    formData.title = props.note.title || '';
    formData.content = props.note.content || '';
    formData.priority = props.note.priority || '';
  } else {
    // Reset form for new note
    formData.title = '';
    formData.content = '';
    formData.priority = '';
  }

  // Clear errors
  Object.keys(errors).forEach(key => {
    (errors as any)[key] = '';
  });

  submitError.value = '';
};

// Watch for prop changes
watch(() => props.note, initializeForm, { immediate: true });

onMounted(() => {
  initializeForm();
  // Focus on title input
  setTimeout(() => {
    const titleInput = document.getElementById('noteTitle') as HTMLInputElement;
    if (titleInput) {
      titleInput.focus();
    }
  }, 100);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: true,
    title: isEditing.value ? _ctx.$t('notes.editNote') : _ctx.$t('notes.createNote'),
    size: "large",
    onClose: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('close'))),
    onCancel: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('close')))
  }, {
    footer: _withCtx(() => [
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("button", {
          type: "button",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('close'))),
          class: "btn btn-secondary"
        }, _toDisplayString(_ctx.$t('common.cancel')), 1),
        _createElementVNode("button", {
          type: "submit",
          class: "btn btn-primary",
          disabled: loading.value || !isFormValid.value,
          onClick: handleSubmit
        }, [
          (loading.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_24))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(loading.value ? _ctx.$t('common.saving') : (isEditing.value ? _ctx.$t('common.update') : _ctx.$t('common.create'))), 1)
        ], 8, _hoisted_23)
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("form", {
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("label", _hoisted_2, [
            _cache[6] || (_cache[6] = _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "18",
              height: "18",
              class: "app-form-field__icon"
            }, [
              _createElementVNode("path", {
                fill: "currentColor",
                d: "M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
              })
            ], -1)),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('notes.title')), 1)
          ]),
          _createElementVNode("div", _hoisted_3, [
            _withDirectives(_createElementVNode("input", {
              id: "noteTitle",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.title) = $event)),
              type: "text",
              class: _normalizeClass(["app-form-field__input", { 'app-form-field__input--error': errors.title }]),
              placeholder: _ctx.$t('notes.titlePlaceholder'),
              required: "",
              maxlength: "255"
            }, null, 10, _hoisted_4), [
              [_vModelText, formData.title]
            ])
          ]),
          (errors.title)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _cache[7] || (_cache[7] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "16",
                  height: "16"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(errors.title), 1)
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("label", _hoisted_7, [
            _cache[8] || (_cache[8] = _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "18",
              height: "18",
              class: "app-form-field__icon"
            }, [
              _createElementVNode("path", {
                fill: "currentColor",
                d: "M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"
              })
            ], -1)),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('notes.content')), 1)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _withDirectives(_createElementVNode("textarea", {
              id: "noteContent",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.content) = $event)),
              class: _normalizeClass(["app-form-field__input app-form-field__input--textarea", { 'app-form-field__input--error': errors.content }]),
              placeholder: _ctx.$t('notes.contentPlaceholder'),
              required: "",
              rows: "6",
              maxlength: "2000"
            }, null, 10, _hoisted_9), [
              [_vModelText, formData.content]
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            (errors.content)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _cache[9] || (_cache[9] = _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "16",
                    height: "16"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                    })
                  ], -1)),
                  _createTextVNode(" " + _toDisplayString(errors.content), 1)
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", {
              class: _normalizeClass(["app-form-field__char-count", { 'app-form-field__char-count--warning': formData.content.length > 1800 }])
            }, _toDisplayString(formData.content.length) + "/2000 ", 3)
          ])
        ]),
        _createElementVNode("div", _hoisted_12, [
          _createElementVNode("label", _hoisted_13, [
            _cache[10] || (_cache[10] = _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "18",
              height: "18",
              class: "app-form-field__icon"
            }, [
              _createElementVNode("path", {
                fill: "currentColor",
                d: "M14,19H18V17H16V16H17A1,1 0 0,0 18,15V14A1,1 0 0,0 17,13H15V15H17V16H15A1,1 0 0,0 14,17V18A1,1 0 0,0 15,19H14M6,19H10V17H8V16H9A1,1 0 0,0 10,15V14A1,1 0 0,0 9,13H7V15H9V16H7A1,1 0 0,0 6,17V18A1,1 0 0,0 7,19H6M12,19H13V13H11V15H12V19Z"
              })
            ], -1)),
            _createTextVNode(" " + _toDisplayString(_ctx.$t('notes.priority.label')), 1)
          ]),
          _createElementVNode("div", _hoisted_14, [
            _withDirectives(_createElementVNode("select", {
              id: "notePriority",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.priority) = $event)),
              class: _normalizeClass(["app-form-field__input app-form-field__input--select", { 'app-form-field__input--error': errors.priority }]),
              required: ""
            }, [
              _createElementVNode("option", _hoisted_15, _toDisplayString(_ctx.$t('notes.selectPriority')), 1),
              _createElementVNode("option", _hoisted_16, _toDisplayString(_ctx.$t('notes.priority.low')), 1),
              _createElementVNode("option", _hoisted_17, _toDisplayString(_ctx.$t('notes.priority.normal')), 1),
              _createElementVNode("option", _hoisted_18, _toDisplayString(_ctx.$t('notes.priority.high')), 1),
              _createElementVNode("option", _hoisted_19, _toDisplayString(_ctx.$t('notes.priority.critical')), 1)
            ], 2), [
              [_vModelSelect, formData.priority]
            ])
          ]),
          (errors.priority)
            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                _cache[11] || (_cache[11] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "16",
                  height: "16"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                  })
                ], -1)),
                _createTextVNode(" " + _toDisplayString(errors.priority), 1)
              ]))
            : _createCommentVNode("", true)
        ]),
        (submitError.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
              _cache[12] || (_cache[12] = _createElementVNode("svg", {
                viewBox: "0 0 24 24",
                width: "20",
                height: "20"
              }, [
                _createElementVNode("path", {
                  fill: "currentColor",
                  d: "M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"
                })
              ], -1)),
              _createTextVNode(" " + _toDisplayString(submitError.value), 1)
            ]))
          : _createCommentVNode("", true)
      ], 32)
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})